import { FormattedMessage } from "react-intl";
import { Box, Grid, Typography } from "@mui/material";

import { type SsoFailureReason } from "../../schemas";
import { ApplicationWordArt, AuthenticationContainer, StyledLoginFormContainer } from "../../components";

import { SsoErrorActions } from "./sso-error-actions";

export interface LoginSsoErrorTemplateProps {
	readonly reason: SsoFailureReason;
	readonly troubleshootUrl?: { pathname: string; hash: string } | null;
}

export const LoginSsoErrorTemplate: React.FC<LoginSsoErrorTemplateProps> = ({ reason, troubleshootUrl = null }) => {
	return (
		<AuthenticationContainer>
			<StyledLoginFormContainer maxWidth="sm">
				<Grid container direction="column" gap={6}>
					<Box sx={{ margin: "auto" }}>
						<ApplicationWordArt width={300} />
					</Box>

					<Grid container direction="column" gap={2}>
						<Typography color="white" fontWeight="bold" variant="h1" sx={{ fontSize: "1.5rem" }}>
							<FormattedMessage id="login-sso-error.header" defaultMessage="Single Sign-On Error" />
						</Typography>

						<Typography color="white">
							<FormattedMessage
								id="login-sso-error.subheader"
								defaultMessage="We're sorry, but we were unable to log you in using Single Sign-On. Please try again"
							/>
						</Typography>

						{reason === "timeout" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage id="login-sso-error.timeout" defaultMessage="The request timed out" />
							</Typography>
						)}

						{reason === "invalid-token" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage
									id="login-sso-error.invalid-token"
									defaultMessage="The token you supplied was invalid"
								/>
							</Typography>
						)}

						{reason === "no-token" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage id="login-sso-error.no-token" defaultMessage="No token was supplied" />
							</Typography>
						)}

						{reason === "unknown" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage id="login-sso-error.unknown-error" defaultMessage="An unknown error occurred" />
							</Typography>
						)}

						{reason === "No Roles Provided" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage
									id="login-sso-error.no-roles-provided"
									defaultMessage="Your identity provider did not send any roles for your account. Please contact your administrator."
								/>
							</Typography>
						)}

						{reason === "IdP Site Not Found" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage
									id="login-sso-error.idp-site-not-found"
									defaultMessage="Your identity provider did not send a primary site for your account. Please contact your administrator."
								/>
							</Typography>
						)}

						{reason === "Not all sites are part of the company" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage
									id="login-sso-error.sites-not-in-company"
									defaultMessage="One or more sites sent by your identity provider are not part of your company. Please contact your administrator."
								/>
							</Typography>
						)}

						{reason === "No SAML attribute values matched" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage
									id="login-sso-error.no-saml-attribute-values-matched"
									defaultMessage="None of the attributes sent by your identity provider matched your company's access configuration. Please contact your administrator."
								/>
							</Typography>
						)}

						{reason === "User Not Found" && (
							<Typography color="white" fontWeight="bold">
								<FormattedMessage
									id="login-sso-error.user-not-found"
									defaultMessage="Your account has not been set up in PackNet. Please contact your administrator."
								/>
							</Typography>
						)}

						<SsoErrorActions troubleshootUrl={troubleshootUrl} />
					</Grid>
				</Grid>
			</StyledLoginFormContainer>
		</AuthenticationContainer>
	);
};
